var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useMemo, useRef, useState, useEffect, } from "react";
import { MultiColumnList, Select, TextLink } from "@folio/stripes/components";
import { useIntl } from "react-intl";
import { SearchAndSortNoResultsMessage as NoResultsMessage, } from "@folio/stripes/smart-components";
import { getColumnId } from "../types/ColumnDefinition";
import { toMclSortDirection } from "../utils/titleSort";
import { MCL_ROW_MARGIN, composeColumnWidths, relativeWidthBudget, resolveColumnWeights, resolveFixedColumnWidths, resolveRelativeColumnIds, } from "../utils/columnPrefs";
import { useContainerWidth } from "../hooks/useContainerWidth";
import { useColumnPrefs } from "../hooks/useColumnPrefs";
import { ColumnSettings } from "./ColumnSettings";
import { ColumnWidthToggle } from "./ColumnWidthToggle";
import { ColumnResizeOverlay } from "./ColumnResizeOverlay";
import { TableLoadingOverlay } from "./TableLoadingOverlay";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pageSize";
import css from "./TableComponent.module.css";
var PAGE_SIZE_DATA_OPTIONS = PAGE_SIZE_OPTIONS.map(function (size) { return ({
    value: String(size),
    label: String(size),
}); });
function TableComponent(_a) {
    var _this = this;
    var data = _a.data, columns = _a.columns, _b = _a.pageSize, pageSize = _b === void 0 ? DEFAULT_PAGE_SIZE : _b, _c = _a.totalCount, totalCount = _c === void 0 ? 0 : _c, onNeedMoreData = _a.onNeedMoreData, onPageSizeChange = _a.onPageSizeChange, searchTerm = _a.searchTerm, filterPaneIsVisible = _a.filterPaneIsVisible, toggleFilterPane = _a.toggleFilterPane, source = _a.source, resetPaginationKey = _a.resetPaginationKey, columnWidths = _a.columnWidths, onRowClick = _a.onRowClick, sortBy = _a.sortBy, sortDir = _a.sortDir, onSort = _a.onSort, rowClassResolver = _a.rowClassResolver, isFetching = _a.isFetching, emptyMessage = _a.emptyMessage, columnPrefsKey = _a.columnPrefsKey, controlledOffset = _a.offset;
    var formatMessage = useIntl().formatMessage;
    var _d = useState(0), internalOffset = _d[0], setInternalOffset = _d[1];
    var isOffsetControlled = controlledOffset !== undefined;
    var offset = isOffsetControlled ? controlledOffset : internalOffset;
    var _f = useState("none"), columnEditMode = _f[0], setColumnEditMode = _f[1];
    var isColumnMenuOpen = columnEditMode === "columns";
    var isEditingWidths = columnEditMode === "width";
    var wrapperRef = useRef(null);
    var containerWidth = useContainerWidth(wrapperRef);
    var columnPrefs = useColumnPrefs(columnPrefsKey, columns);
    useEffect(function () {
        setInternalOffset(0);
    }, [resetPaginationKey]);
    useEffect(function () {
        setColumnEditMode("none");
    }, [columnPrefsKey]);
    useEffect(function () {
        if (!isEditingWidths) {
            return undefined;
        }
        var handleKeyDown = function (event) {
            if (event.key === "Escape") {
                setColumnEditMode("none");
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return function () { return document.removeEventListener("keydown", handleKeyDown); };
    }, [isEditingWidths]);
    var handleNeedMoreData = function (_amount, index) { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (onNeedMoreData) {
                onNeedMoreData(index);
            }
            if (!isOffsetControlled) {
                setInternalOffset(index);
            }
            return [2 /*return*/];
        });
    }); };
    var handlePageSizeChange = useCallback(function (event) {
        if (!isOffsetControlled) {
            setInternalOffset(0);
        }
        onPageSizeChange === null || onPageSizeChange === void 0 ? void 0 : onPageSizeChange(Number(event.target.value));
    }, [isOffsetControlled, onPageSizeChange]);
    var isFailed = !!(source === null || source === void 0 ? void 0 : source.failure());
    var resultsStatusMessage = source ? (React.createElement("div", { "data-test-user-search-no-results-message": true },
        React.createElement(NoResultsMessage, { source: source, searchTerm: searchTerm !== null && searchTerm !== void 0 ? searchTerm : "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane }))) : ("no source yet");
    var columnMapping = useMemo(function () {
        return columns.reduce(function (acc, col) {
            acc[getColumnId(col)] = col.name;
            return acc;
        }, {});
    }, [columns]);
    var renderValue = function (value) {
        if (typeof value === "string" || typeof value === "number") {
            return value;
        }
        if (React.isValidElement(value)) {
            return value;
        }
        return String(value !== null && value !== void 0 ? value : "");
    };
    var formatter = useMemo(function () {
        var wrapContentInClickableLink = function (cellContent, rowItem, onClickHandler) { return (React.createElement(TextLink, { onClick: function (e) {
                e.stopPropagation();
                onClickHandler(rowItem);
            } }, cellContent)); };
        return columns.reduce(function (formattersAccumulator, column) {
            formattersAccumulator[getColumnId(column)] = function (rowItem) {
                if (column.cellRenderer) {
                    var renderedContent = column.cellRenderer(rowItem);
                    var shouldWrapInLink = column.clickable && column.onClick;
                    if (shouldWrapInLink && column.onClick) {
                        return wrapContentInClickableLink(renderedContent, rowItem, column.onClick);
                    }
                    return renderedContent;
                }
                var cellValue = rowItem[column.key];
                var isValueClickable = column.clickable &&
                    column.onClick &&
                    (typeof cellValue === "string" || typeof cellValue === "number");
                if (isValueClickable && column.onClick) {
                    return wrapContentInClickableLink(cellValue, rowItem, column.onClick);
                }
                return renderValue(cellValue);
            };
            return formattersAccumulator;
        }, {});
    }, [columns]);
    var visibleColumns = columnPrefs.visibleColumnIds;
    var lockedColumnIds = useMemo(function () { return columns.filter(function (col) { return col.locked; }).map(function (col) { return getColumnId(col); }); }, [columns]);
    var fixedColumnWidths = useMemo(function () {
        return resolveFixedColumnWidths(visibleColumns, columnWidths, lockedColumnIds);
    }, [visibleColumns, columnWidths, lockedColumnIds]);
    var relativeColumnIds = useMemo(function () { return resolveRelativeColumnIds(visibleColumns, fixedColumnWidths); }, [visibleColumns, fixedColumnWidths]);
    var widthBudget = relativeWidthBudget(containerWidth, fixedColumnWidths);
    var columnWeights = useMemo(function () {
        return resolveColumnWeights(relativeColumnIds, columnWidths, columnPrefs.widths, widthBudget);
    }, [relativeColumnIds, columnWidths, columnPrefs.widths, widthBudget]);
    var effectiveColumnWidths = useMemo(function () {
        return columnPrefs.isEnabled
            ? composeColumnWidths(visibleColumns, fixedColumnWidths, columnWeights)
            : columnWidths;
    }, [
        columnPrefs.isEnabled,
        visibleColumns,
        fixedColumnWidths,
        columnWeights,
        columnWidths,
    ]);
    /**
     * The two modes are exclusive: widths stay locked while the column menu is open.
     * Stripes reports onToggle both for the trigger click and for "closing because you
     * clicked elsewhere"; the latter arrives without an event, because Popdown.menuBlur
     * calls closeMenu() with no argument (stripes-components lib/Dropdown/Popdown.js).
     * Treating every report as a plain toggle reopens the menu the moment switching to
     * width editing closes it, so the outcome depends on the order of the two updates.
     * MG-21 covers this, so a change in that behaviour surfaces as a red test, not silently.
     */
    var toggleColumnMenu = useCallback(function (event) {
        setColumnEditMode(function (current) {
            if (!event) {
                return current === "columns" ? "none" : current;
            }
            return current === "columns" ? "none" : "columns";
        });
    }, []);
    var toggleWidthEditing = useCallback(function () {
        setColumnEditMode(function (current) { return (current === "width" ? "none" : "width"); });
    }, []);
    var canResizeColumns = isEditingWidths &&
        columnPrefs.isEnabled &&
        Object.keys(columnWeights).length > 1;
    var sortEnabled = !!onSort;
    var nonInteractiveHeaders = useMemo(function () { return columns.filter(function (col) { return !col.sortKey; }).map(function (col) { return getColumnId(col); }); }, [columns]);
    var sortableFields = useMemo(function () { return columns.filter(function (col) { return col.sortKey; }).map(function (col) { return getColumnId(col); }); }, [columns]);
    var sortedColumnId = useMemo(function () {
        var active = columns.find(function (col) { return col.sortKey && col.sortKey === sortBy; });
        return active ? getColumnId(active) : undefined;
    }, [columns, sortBy]);
    var handleHeaderClick = useCallback(function (_e, meta) {
        var clickedId = meta.name;
        var column = columns.find(function (col) { return getColumnId(col) === clickedId; });
        if ((column === null || column === void 0 ? void 0 : column.sortKey) && onSort) {
            onSort(column.sortKey);
        }
    }, [columns, onSort]);
    var sortProps = sortEnabled
        ? {
            onHeaderClick: handleHeaderClick,
            sortedColumn: sortedColumnId,
            sortDirection: sortDir ? toMclSortDirection(sortDir) : undefined,
            nonInteractiveHeaders: nonInteractiveHeaders,
            sortableFields: sortableFields,
            showSortIndicator: true,
        }
        : {};
    var rowFormatter = useCallback(function (props) {
        var extraClass = rowClassResolver === null || rowClassResolver === void 0 ? void 0 : rowClassResolver(props.rowData);
        var className = [props.rowClass, extraClass].filter(Boolean).join(" ");
        return (React.createElement("div", __assign({ className: className }, props.rowProps), props.cells));
    }, [rowClassResolver]);
    var rowFormatterProps = rowClassResolver ? { rowFormatter: rowFormatter } : {};
    return (React.createElement("div", { className: css.tableWrapper, ref: wrapperRef },
        columnPrefs.isEnabled && (React.createElement("div", { className: css.tableToolbar },
            isEditingWidths && (React.createElement("span", { className: css.widthHint }, formatMessage({
                id: "ui-br-legaldeposits.columns.width.hint",
            }))),
            React.createElement(ColumnSettings, { columns: columnPrefs.manageableColumns, isCustomized: columnPrefs.isCustomized, onToggle: columnPrefs.toggleColumn, onMove: columnPrefs.moveColumn, onReset: columnPrefs.resetColumns, open: isColumnMenuOpen, onOpenToggle: toggleColumnMenu }),
            React.createElement(ColumnWidthToggle, { active: isEditingWidths, onToggle: toggleWidthEditing }))),
        canResizeColumns && (React.createElement(ColumnResizeOverlay, { containerRef: wrapperRef, visibleColumnIds: visibleColumns, weights: columnWeights, percentBase: (containerWidth !== null && containerWidth !== void 0 ? containerWidth : 0) - MCL_ROW_MARGIN, onChange: columnPrefs.setColumnWidths, onCancel: columnPrefs.revertColumnWidths })),
        React.createElement(MultiColumnList
        /*
         * MCL keeps the widths of columns that are no longer rendered in its own state and,
         * whenever the key count stops matching the column count, overwrites the incoming
         * widths with those stale ones (MCLRenderer.componentDidUpdate). After hiding a
         * column the mismatch is permanent, so resizing silently stops working. Remounting
         * on a change of the visible set clears that state; MG-21 covers the regression.
         */
        , __assign({ 
            /*
             * MCL keeps the widths of columns that are no longer rendered in its own state and,
             * whenever the key count stops matching the column count, overwrites the incoming
             * widths with those stale ones (MCLRenderer.componentDidUpdate). After hiding a
             * column the mismatch is permanent, so resizing silently stops working. Remounting
             * on a change of the visible set clears that state; MG-21 covers the regression.
             */
            key: visibleColumns.join("|"), contentData: isFailed ? [] : data, columnMapping: columnMapping, formatter: formatter, visibleColumns: visibleColumns, columnWidths: effectiveColumnWidths, pagingType: "prev-next", isEmptyMessage: isFailed
                ? resultsStatusMessage
                : (emptyMessage !== null && emptyMessage !== void 0 ? emptyMessage : resultsStatusMessage), pagingOffset: offset, pageAmount: pageSize, totalCount: totalCount, onNeedMoreData: function (pageAmount, newOffset) {
                handleNeedMoreData(pageAmount, newOffset);
            }, interactive: !!onRowClick, onRowClick: onRowClick
                ? function (_e, row) { return onRowClick(row); }
                : undefined }, rowFormatterProps, sortProps)),
        onPageSizeChange && !isFailed && data.length > 0 && (React.createElement("div", { className: css.pageSizeRow },
            React.createElement("span", { className: css.pageSizeLabel }, formatMessage({ id: "ui-br-legaldeposits.table.pageSize.label" })),
            React.createElement("div", { className: css.pageSizeSelect },
                React.createElement(Select, { value: String(pageSize), dataOptions: PAGE_SIZE_DATA_OPTIONS, onChange: handlePageSizeChange, "aria-label": formatMessage({
                        id: "ui-br-legaldeposits.table.pageSize.label",
                    }), marginBottom0: true })))),
        React.createElement(TableLoadingOverlay, { visible: !!isFetching && data.length > 0 })));
}
export default TableComponent;
